import React, { useState, useEffect } from 'react';
import { bool } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { LoadingButton } from '@hmhco/loading-button';
import {
  getCurrentOrg,
  getCurrentCountryCode,
} from '../../../../store/selectors/CountryStateOrg';
import useStyles from './EarlyAccessButtons.styles';
import {
  setEarlyAccessError,
  setEarlyAccessLoading,
  setEarlyAccessStep,
  setEarlyAccessSuccess,
} from '../../../../store/actions/EarlyAccessActions';
import {
  getEarlyAccessStep,
  getIsEarlyAccessEmailValid,
  getEarlyAccessEmail,
} from '../../../../store/selectors/EarlyAccessPageState';
import useEarlyAccessRegistrationToken from '../../../../hooks/useEarlyAccessRegistrationToken.hook';

const ACCOUNT_EXISTS_ERROR =
  'Email entered already has active account associated with it. Please use the existing credentials to log in.';

const EarlyAccessButtons = ({ canSubmit }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const earlyAccessStep = useSelector(getEarlyAccessStep);
  const isEmailValid = useSelector(getIsEarlyAccessEmailValid);
  const currentOrg = useSelector(getCurrentOrg);
  const email = useSelector(getEarlyAccessEmail);
  const countryCode = useSelector(getCurrentCountryCode);
  const {
    mutateAsync: requestRegistrationToken,
  } = useEarlyAccessRegistrationToken();

  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async event => {
    event.preventDefault();
    if (earlyAccessStep === 1) {
      dispatch(setEarlyAccessStep(2));
    } else {
      try {
        setIsLoading(true);
        dispatch(setEarlyAccessLoading());
        await requestRegistrationToken({
          orgRefId: currentOrg?.orgRefId,
          pid: currentOrg?.pid,
          orgType: currentOrg?.orgType,
          countryCode,
          email,
        });
        setIsLoading(false);
        dispatch(setEarlyAccessSuccess());
      } catch (error) {
        setIsLoading(false);
        dispatch(
          setEarlyAccessError(error?.serverError === ACCOUNT_EXISTS_ERROR),
        );
      }
    }
  };

  const getButtonText = () => {
    if (earlyAccessStep === 1) {
      return 'earlyAccess.continueButton';
    }
    return 'earlyAccess.submitButton';
  };

  useEffect(() => {
    if (!canSubmit) {
      setButtonDisabled(true);
    } else if (earlyAccessStep === 1) {
      const hasOrg = !!currentOrg?.name;
      setButtonDisabled(!hasOrg);
    } else if (earlyAccessStep === 2 && isEmailValid) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [canSubmit, earlyAccessStep, currentOrg, isEmailValid]);

  return (
    <div className={classes.buttons}>
      <FormattedMessage id={getButtonText()}>
        {([label]) => (
          <LoadingButton
            loading={isLoading}
            disabled={buttonDisabled}
            buttonProps={{
              children: label,
              color: 'primary',
              className: classes.button,
              variant: 'contained',
              size: 'small',
              onClick: handleClick,
              type: 'submit',
            }}
            loadingText={label}
          />
        )}
      </FormattedMessage>
    </div>
  );
};

EarlyAccessButtons.propTypes = {
  canSubmit: bool,
};

EarlyAccessButtons.defaultProps = {
  canSubmit: true,
};

export default EarlyAccessButtons;
